import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Upload, Eye, Save, Building2, Image as ImageIcon, Truck } from "lucide-react";

export default function CompanyConfig() {
  const [info, setInfo] = useState({
    razon_social: "", nombre_comercial: "", ruc: "", direccion: "", ciudad: "",
    telefono: "", email_contacto: "", web: "", obligado_contabilidad: "SI", contribuyente_especial: "",
    transportista_razon_social: "", transportista_ruc: "", transportista_tipo_identificacion: "ruc",
    transportista_placa: "", transportista_dir_partida: "",
    dias_alerta_vencimiento_corto: 180,
  });
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoMeta, setLogoMeta] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const fileRef = useRef(null);

  const loadAll = async () => {
    try {
      const [infoRes, logoRes] = await Promise.all([
        api.get("/company/info"),
        api.get("/company/logo").catch(() => ({ data: null })),
      ]);
      setInfo((prev) => ({ ...prev, ...infoRes.data }));
      setLogoUrl(logoRes.data?.data_url || null);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingInitial(false); }
  };
  useEffect(() => { loadAll(); }, []);

  const guardarInfo = async () => {
    setLoading(true);
    try {
      await api.patch("/company/info", info);
      toast.success("Datos de empresa guardados");
      loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const onLogoChange = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Seleccione una imagen válida");
    if (f.size > 5 * 1024 * 1024) return toast.error("Imagen demasiado grande (>5MB)");
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLogoMeta(data);
      toast.success(`Logo actualizado · ${data.size?.[0]}×${data.size?.[1]}px (auto-recortado)`);
      // refresh logo preview con cache-buster
      const { data: logoRes } = await api.get("/company/logo");
      setLogoUrl(logoRes.data_url);
      setPreviewUrl(null); // forzar regenerar preview
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const generarPreview = async () => {
    setLoading(true);
    try {
      const res = await api.get("/company/preview-pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPreviewUrl(url);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="company-config-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Configuración</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Empresa</h1>
        <p className="text-sm text-[#4a5c53] mt-1">Logotipo y datos institucionales que aparecen en facturas, retenciones y demás documentos electrónicos.</p>
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <Card className="p-5 bg-white border-[#d1dbd6]">
            <div className="flex items-center gap-2 mb-4 text-[#205c45]">
              <Building2 size={18} />
              <h2 className="font-semibold">Datos de la empresa</h2>
              {loadingInitial && (
                <span className="ml-2 text-[11px] text-[#8ba397] animate-pulse">Cargando datos...</span>
              )}
            </div>
            {loadingInitial ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="space-y-1.5">
                    <div className="h-3 w-24 bg-[#e6ecea] rounded animate-pulse" />
                    <div className="h-9 bg-[#f0f4f2] rounded animate-pulse" />
                  </div>
                ))}
              </div>
            ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-1.5">
                <Label>Razón social</Label>
                <Input value={info.razon_social || ""} onChange={(e) => setInfo({ ...info, razon_social: e.target.value })} data-testid="empresa-razon-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Nombre comercial</Label>
                <Input value={info.nombre_comercial || ""} onChange={(e) => setInfo({ ...info, nombre_comercial: e.target.value })} data-testid="empresa-nombre-input" />
              </div>
              <div className="space-y-1.5">
                <Label>RUC</Label>
                <Input value={info.ruc || ""} onChange={(e) => setInfo({ ...info, ruc: e.target.value })} data-testid="empresa-ruc-input" />
              </div>
              <div className="md:col-span-2 space-y-1.5">
                <Label>Dirección matriz</Label>
                <Input value={info.direccion || ""} onChange={(e) => setInfo({ ...info, direccion: e.target.value })} data-testid="empresa-direccion-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Ciudad</Label>
                <Input value={info.ciudad || ""} onChange={(e) => setInfo({ ...info, ciudad: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Teléfono</Label>
                <Input value={info.telefono || ""} onChange={(e) => setInfo({ ...info, telefono: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Email contacto</Label>
                <Input value={info.email_contacto || ""} onChange={(e) => setInfo({ ...info, email_contacto: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Sitio web</Label>
                <Input value={info.web || ""} onChange={(e) => setInfo({ ...info, web: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Obligado a llevar contabilidad</Label>
                <Select value={info.obligado_contabilidad || "SI"} onValueChange={(v) => setInfo({ ...info, obligado_contabilidad: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="SI">SI</SelectItem>
                    <SelectItem value="NO">NO</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Contribuyente especial (Resolución)</Label>
                <Input value={info.contribuyente_especial || ""} onChange={(e) => setInfo({ ...info, contribuyente_especial: e.target.value })} placeholder="Vacío si no aplica" />
              </div>
              <div className="space-y-1.5" title="Días de anticipación para alertar en Notas de Pedido y Facturas cuando se usen lotes con vencimiento próximo.">
                <Label>Alerta vencimiento corto (días)</Label>
                <Input
                  type="number" min="0" step="1"
                  value={info.dias_alerta_vencimiento_corto ?? 180}
                  onChange={(e) => setInfo({ ...info, dias_alerta_vencimiento_corto: e.target.value === "" ? null : parseInt(e.target.value) })}
                  placeholder="180"
                  data-testid="dias-alerta-vencimiento-input"
                />
                <p className="text-xs text-[#5c7368]">Los lotes que venzan dentro de este período mostrarán un aviso al vendedor.</p>
              </div>
            </div>
            )}
            <div className="flex justify-end mt-5">
              <Button onClick={guardarInfo} disabled={loading || loadingInitial} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="empresa-guardar-btn">
                <Save size={16} className="mr-2" /> Guardar cambios
              </Button>
            </div>
          </Card>

          <Card className="p-5 bg-white border-[#d1dbd6]">
            <div className="flex items-center gap-2 mb-1 text-[#205c45]">
              <Truck size={18} />
              <h2 className="font-semibold">Datos por defecto para Guía de Remisión</h2>
            </div>
            <p className="text-xs text-[#5c7368] mb-4">
              Se usarán al generar guías automáticas desde una factura. Los campos siguen siendo editables al momento de emitir cada guía.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 space-y-1.5">
                <Label>Razón social del transportista</Label>
                <Input
                  value={info.transportista_razon_social || ""}
                  onChange={(e) => setInfo({ ...info, transportista_razon_social: e.target.value })}
                  placeholder="Empresa o persona que transporta"
                  data-testid="transportista-razon-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Tipo de identificación</Label>
                <Select
                  value={info.transportista_tipo_identificacion || "ruc"}
                  onValueChange={(v) => setInfo({ ...info, transportista_tipo_identificacion: v })}
                >
                  <SelectTrigger data-testid="transportista-tipo-id"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ruc">RUC</SelectItem>
                    <SelectItem value="cedula">Cédula</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>RUC / Cédula transportista</Label>
                <Input
                  value={info.transportista_ruc || ""}
                  onChange={(e) => setInfo({ ...info, transportista_ruc: e.target.value })}
                  data-testid="transportista-ruc-input"
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Placa del vehículo</Label>
                <Input
                  value={info.transportista_placa || ""}
                  onChange={(e) => setInfo({ ...info, transportista_placa: e.target.value.toUpperCase() })}
                  placeholder="ABC-1234"
                  data-testid="transportista-placa-input"
                  className="font-mono"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Dirección de partida</Label>
                <Input
                  value={info.transportista_dir_partida || ""}
                  onChange={(e) => setInfo({ ...info, transportista_dir_partida: e.target.value })}
                  placeholder="Ej: Bodega matriz, Av. Principal 123"
                  data-testid="transportista-dir-partida-input"
                />
              </div>
            </div>
            <div className="flex justify-end mt-5">
              <Button onClick={guardarInfo} disabled={loading || loadingInitial} className="bg-[#205c45] hover:bg-[#1a4a37]" data-testid="transportista-guardar-btn">
                <Save size={16} className="mr-2" /> Guardar transporte
              </Button>
            </div>
          </Card>

          <Card className="p-5 bg-white border-[#d1dbd6]">
            <div className="flex items-center gap-2 mb-4 text-[#205c45]">
              <ImageIcon size={18} />
              <h2 className="font-semibold">Logotipo</h2>
            </div>
            <div className="flex flex-col md:flex-row gap-5 items-start">
              <div className="border border-dashed border-[#d1dbd6] rounded-md p-4 bg-[#f7f9f8] w-full md:w-1/2 min-h-[180px] flex items-center justify-center">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="max-w-full max-h-40 object-contain" data-testid="empresa-logo-preview" />
                ) : (
                  <div className="text-sm text-[#8ba397]">Aún no hay logo</div>
                )}
              </div>
              <div className="flex-1 space-y-3 text-sm">
                <p className="text-[#4a5c53]">Sube un logo en formato PNG, JPG o SVG. El sistema recortará automáticamente el padding transparente para que se vea grande en los documentos electrónicos.</p>
                <ul className="text-xs text-[#5c7368] list-disc pl-4 space-y-0.5">
                  <li>Tamaño máximo: 5MB</li>
                  <li>Recomendado: PNG con fondo transparente, alta resolución (≥1500px de ancho)</li>
                  <li>Aplica a todos los documentos: facturas, notas crédito/débito, retenciones, guías</li>
                </ul>
                <input ref={fileRef} type="file" accept="image/*" onChange={onLogoChange} className="hidden" data-testid="empresa-logo-input" />
                <Button onClick={() => fileRef.current?.click()} disabled={loading} variant="outline" data-testid="empresa-logo-upload-btn">
                  <Upload size={16} className="mr-2" /> Cambiar logo
                </Button>
                {logoMeta && (
                  <p className="text-xs text-[#205c45]">✓ Último upload: {logoMeta.size?.[0]}×{logoMeta.size?.[1]}px (ratio w/h: {logoMeta.ratio})</p>
                )}
              </div>
            </div>
          </Card>
        </div>

        <Card className="p-5 bg-white border-[#d1dbd6] xl:col-span-1">
          <div className="flex items-center gap-2 mb-4 text-[#205c45]">
            <Eye size={18} />
            <h2 className="font-semibold">Vista previa del PDF</h2>
          </div>
          <p className="text-xs text-[#5c7368] mb-3">Genera un mock-up de factura electrónica con tu logo y datos para verificar cómo se verán los documentos antes de emitirlos.</p>
          <Button onClick={generarPreview} disabled={loading} className="w-full bg-[#205c45] hover:bg-[#1a4a37]" data-testid="empresa-preview-btn">
            <Eye size={16} className="mr-2" /> Generar vista previa
          </Button>
          {previewUrl && (
            <div className="mt-4 space-y-2">
              <iframe title="Vista previa PDF" src={previewUrl} className="w-full h-[480px] border border-[#d1dbd6] rounded" data-testid="empresa-preview-iframe" />
              <a href={previewUrl} download="vista-previa-empresa.pdf" className="text-sm text-[#205c45] underline">Descargar PDF</a>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
